// src/pages/RequestMoneyPage.js
import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'

function RequestMoneyPage() {
  // 1) Retrieve lang & openInviteModal from parent's Outlet context
  const { lang, openInviteModal } = useOutletContext() || {}

  const [toUserEmail, setToUserEmail] = useState('')
  const [amount, setAmount] = useState('')

  // For showing a dynamic warning if the amount < 50
  const [amountWarning, setAmountWarning] = useState('')

  // For the dynamic email suggestions
  const [emailSearchTerm, setEmailSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Logged-in user
  const user = JSON.parse(localStorage.getItem('user')) || {}
  const token = localStorage.getItem('token')

  // Success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // Error modal
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // 2) Basic translations (added "minAmountMsg")
  const translations = {
    en: {
      pageTitle: 'Request Money',
      pageSubtitle: 'Fill out the form below to request money from another user.',
      requestFrom: 'Request From',
      requestFromPlaceholder: 'Email of the person to request money from',
      amountLabel: 'Amount (¥)',
      amountPlaceholder: 'Enter amount in yen',
      requestButton: 'Request',
      successTitle: 'Request Created',
      successMessage: 'Your money request was created successfully!',
      errorTitle: 'Error',
      errorDefault:
        'An error occurred while creating the request. Please try again.',
      pleaseLogin: 'Please log in first.',
      ok: 'OK',
      inviteFriendLabel: 'Invite your friend',
      minAmountMsg: 'The minimum amount for requesting money is 50 yen.',
    },
    ja: {
      pageTitle: '支払い依頼',
      pageSubtitle: '他のユーザーに支払いを依頼する場合、以下のフォームに入力してください。',
      requestFrom: '依頼先のメールアドレス',
      requestFromPlaceholder: '支払いを依頼する相手のメールアドレス',
      amountLabel: '金額 (¥)',
      amountPlaceholder: '金額を入力してください',
      requestButton: '依頼する',
      successTitle: 'リクエストが作成されました',
      successMessage: '支払いリクエストが正常に作成されました！',
      errorTitle: 'エラー',
      errorDefault:
        'リクエストの作成中にエラーが発生しました。もう一度お試しください。',
      pleaseLogin: '最初にログインしてください。',
      ok: 'OK',
      inviteFriendLabel: '友達を招待',
      minAmountMsg: 'リクエストの最低金額は50円です。',
    },
    pt: {
      pageTitle: 'Solicitar Dinheiro',
      pageSubtitle:
        'Preencha o formulário abaixo para solicitar dinheiro de outro usuário.',
      requestFrom: 'Solicitar De',
      requestFromPlaceholder: 'E-mail da pessoa para solicitar dinheiro',
      amountLabel: 'Valor (¥)',
      amountPlaceholder: 'Digite o valor em ienes',
      requestButton: 'Solicitar',
      successTitle: 'Solicitação Criada',
      successMessage: 'Sua solicitação de dinheiro foi criada com sucesso!',
      errorTitle: 'Erro',
      errorDefault:
        'Ocorreu um erro ao criar a solicitação. Por favor, tente novamente.',
      pleaseLogin: 'Por favor, faça login primeiro.',
      ok: 'OK',
      inviteFriendLabel: 'Convide seu amigo',
      minAmountMsg: 'O valor mínimo para solicitar dinheiro é 50 ienes.',
    },
  }

  // 3) Pick the appropriate translation dictionary
  const t = translations[lang] || translations.en

  // 4) On input change => show suggestions dropdown if length > 1
  const handleRecipientEmailChange = (e) => {
    const value = e.target.value
    setToUserEmail(value)
    setEmailSearchTerm(value)
    setShowSuggestions(value.length > 1)
  }

  // 5) Fetch suggestions or show special "inviteFriend"
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (emailSearchTerm.length < 2) {
        setSuggestions([])
        return
      }
      try {
        const res = await axios.get(
          `https://payme-backend-713827320947.asia-northeast1.run.app/api/users/search?query=${encodeURIComponent(
            emailSearchTerm
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const results = res.data // e.g. [{ id, email, name }, ...]

        if (results.length === 0) {
          // No matches => show "inviteFriend"
          setSuggestions([{ id: 'inviteFriend', email: '', name: '' }])
        } else {
          setSuggestions(results)
        }
      } catch (error) {
        console.error('Error fetching email suggestions:', error)
        setSuggestions([])
      }
    }
    fetchSuggestions()
  }, [emailSearchTerm, token])

  // Format suggestion => "Name - email" or "inviteFriend"
  const formatSuggestion = (item) => {
    if (item.id === 'inviteFriend') {
      return t.inviteFriendLabel
    }
    return item.name ? `${item.name} - ${item.email}` : item.email
  }

  const handleSelectSuggestion = (item) => {
    // if it's inviteFriend => open parent's modal
    if (item.id === 'inviteFriend') {
      setShowSuggestions(false)
      if (openInviteModal) {
        openInviteModal()
      }
      return
    }
    setToUserEmail(item.email)
    setShowSuggestions(false)
  }

  // Handle amount change, show a warning if < 50
  const handleAmountChange = (e) => {
    const val = e.target.value
    setAmount(val)

    const numericVal = parseInt(val, 10)
    if (!isNaN(numericVal) && numericVal > 0 && numericVal < 50) {
      setAmountWarning(t.minAmountMsg)
    } else {
      setAmountWarning('')
    }
  }

  // 6) Submit request
  const handleRequestMoney = async (e) => {
    e.preventDefault()

    // Convert amount to int
    const parsedAmount = parseInt(amount, 10)

    // Check if valid or less than 50
    if (!parsedAmount || parsedAmount < 50) {
      setErrorMessage(t.minAmountMsg)
      setShowErrorModal(true)
      return
    }

    try {
      await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/create',
        {
          from_user_id: user.id,
          to_user_email: toUserEmail,
          amount: parsedAmount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Show success modal
      setShowSuccessModal(true)

      // Clear form
      setToUserEmail('')
      setAmount('')
      setAmountWarning('')
    } catch (error) {
      console.error('Error creating request:', error)
      setErrorMessage(t.errorDefault)
      setShowErrorModal(true)
    }
  }

  // 7) Modal close handlers
  const handleSuccessOk = () => {
    setShowSuccessModal(false)
  }

  const handleErrorOk = () => {
    setShowErrorModal(false)
  }

  // If no user logged in
  if (!user || !user.id) {
    return <p className="m-4 text-red-600">{t.pleaseLogin}</p>
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-6 space-y-6">
      {/* Title + Description */}
      <div>
        <h2 className="text-2xl font-semibold">{t.pageTitle}</h2>
        <p className="text-gray-600">{t.pageSubtitle}</p>
      </div>

      {/* Form Card */}
      <div className="bg-white shadow rounded p-6">
        <form onSubmit={handleRequestMoney} className="space-y-5">
          {/* Request From (email) */}
          <div>
            <label
              htmlFor="recipient-email"
              className="block mb-1 text-gray-700 font-medium"
            >
              {t.requestFrom}
            </label>
            <div className="relative">
              <input
                id="recipient-email"
                type="email"
                value={toUserEmail}
                onChange={handleRecipientEmailChange}
                placeholder={t.requestFromPlaceholder}
                required
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              />
              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 bg-white border border-gray-200 rounded mt-1 w-full">
                  {suggestions.map((item) => (
                    <div
                      key={item.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      {formatSuggestion(item)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Amount Field */}
          <div>
            <label
              htmlFor="amount"
              className="block mb-1 text-gray-700 font-medium"
            >
              {t.amountLabel}
            </label>
            {/* Show warning if amount < 50 */}
            {amountWarning && (
              <p className="text-red-600">{amountWarning}</p>
            )}
            <input
              id="amount"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              placeholder={t.amountPlaceholder}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white font-medium px-6 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              {t.requestButton}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-96 max-w-full p-6 relative mx-4">
            <h4 className="text-xl font-semibold mb-4">{t.successTitle}</h4>
            <p>{t.successMessage}</p>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleSuccessOk}
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-96 max-w-full p-6 relative mx-4">
            <h4 className="text-xl font-semibold mb-4 text-red-600">
              {t.errorTitle}
            </h4>
            <p>{errorMessage}</p>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleErrorOk}
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RequestMoneyPage
